import React, { useRef, useState } from "react";

interface OtpInputProps {
  length?: number;
  onComplete: (code: string) => void;
  autofocus?: boolean;
  onReset: (code: string) => void;
}

export default function OtpInput({
  length = 5,
  onComplete,
  autofocus = true,
  onReset,
}: OtpInputProps) {
  const [values, setValues] = useState(Array(length).fill(""));
  const [error, setError] = useState("");
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const validate = (val: string) => /^\d$/.test(val);

  const handleChange = (idx: number, val: string) => {
    if (val.length > 1) return;
    if (val === "") {
      updateValue(idx, "");
      return;
    }
    if (!validate(val)) {
      setError("Only numbers allowed");
      updateValue(idx, "");
      refs.current[idx]?.focus();
      return;
    }
    setError("");
    updateValue(idx, val);

    if (val && refs.current[idx + 1]) refs.current[idx + 1]?.focus();

    const newVals = [...values];
    newVals[idx] = val;
    if (newVals.every((v) => v !== "")) {
      onComplete(newVals.join(""));
    }
  };

  const handleKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (values[idx] === "" && refs.current[idx - 1]) {
        refs.current[idx - 1]?.focus();
      } else {
        updateValue(idx, "");
        onReset("");
      }
    }
  };

  const updateValue = (idx: number, val: string) => {
    setValues((prev) => {
      const copy = [...prev];
      copy[idx] = val;
      return copy;
    });
  };

  const handlePaste = (
    idx: number,
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    setError("");
    setValues((prev) => {
      const next = [...prev];
      for (let i = 0; i < text.length && idx + i < length; i++) {
        next[idx + i] = text[i];
      }
      const code = next.join("");
      if (onComplete && next.every((v) => v !== "")) onComplete(code);
      const firstEmpty = next.findIndex((v) => v === "");
      const focusTo = firstEmpty === -1 ? length - 1 : firstEmpty;
      refs.current[focusTo]?.focus();
      return next;
    });
  };

  return (
    <div className="d-flex px-5">
      <div className="input-group">
        {values.map((v, idx) => (
          <input
            key={idx}
            ref={(el) => {
              refs.current[idx] = el;
            }}
            className="form-control"
            value={v}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={(e) => handlePaste(idx, e)}
            maxLength={1}
            inputMode="numeric"
            pattern="\d*"
            autoFocus={autofocus && idx === 0}
            required
          />
        ))}
      </div>
      {error && <span className="input-group-error">{error}</span>}
    </div>
  );
}
